import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import store from "store";
import Api from "../Services/Api";
import AppHeader from "./AppHeader";

const api = Api.create();

const STATUS_META = {
  pending:    { label: "Payment pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  paid:       { label: "Paid · queued",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  processing: { label: "Generating report", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  delivered:  { label: "Delivered",       cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatSite(siteUrl) {
  if (!siteUrl) return "";
  return siteUrl.startsWith("sc-domain:")
    ? `${siteUrl.replace("sc-domain:", "")} (domain)`
    : siteUrl;
}

/**
 * /reports — list of all reports for the signed-in user.
 * Pending (unpaid) reports have an inline "Complete payment" button that
 * resumes the Stripe flow for that specific report.
 */
export default function ReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingToken, setPayingToken] = useState("");

  useEffect(() => {
    const token = store.get("token");
    if (!token) {
      navigate("/start", { replace: true });
      return;
    }
    api.setAuthToken(token);

    api.listReports().then((resp) => {
      setLoading(false);
      if (resp.ok && Array.isArray(resp.data?.reports)) {
        setReports(resp.data.reports);
      } else {
        setError(resp.data?.error || "Could not load your reports.");
      }
    });
  }, [navigate]);

  const resumePayment = async (reportToken) => {
    setPayingToken(reportToken);
    setError("");
    const resp = await api.createCheckout({ report_token: reportToken });
    const url = resp?.data?.url;
    if (resp.ok && typeof url === "string" && /^https:\/\/(buy|checkout)\.stripe\.com\//.test(url)) {
      window.location.href = url;
    } else {
      setPayingToken("");
      setError(resp?.data?.error || "Could not resume checkout. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Your reports</h1>
            <p className="text-sm text-slate-500">All the scans you&apos;ve started, with their current status.</p>
          </div>
          <button
            onClick={() => navigate("/connected")}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer transition-colors"
          >
            + New scan
          </button>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </div>
        )}

        {loading && <div className="text-sm text-slate-500">Loading…</div>}

        {!loading && reports.length === 0 && !error && (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <div className="text-3xl mb-3">📡</div>
            <div className="text-base font-semibold text-slate-900 mb-1">No reports yet</div>
            <p className="text-sm text-slate-500 mb-5">Start your first scan — it takes under 2 minutes.</p>
            <button
              onClick={() => navigate("/connected")}
              className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer"
            >
              Start a scan
            </button>
          </div>
        )}

        <div className="space-y-3">
          {reports.map((r) => {
            const meta = STATUS_META[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
            const isPending = r.status === "pending";
            return (
              <div
                key={r.token}
                className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${meta.cls}`}>
                      {meta.label}
                    </span>
                    <span className="text-xs text-slate-400">
                      Created {formatDate(r.created_at)}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {formatSite(r.site_url)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.paid_at ? <>Paid {formatDate(r.paid_at)}</> : "Not paid yet"}
                    {r.delivered_at && <> · Delivered {formatDate(r.delivered_at)}</>}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {isPending && (
                    <button
                      onClick={() => resumePayment(r.token)}
                      disabled={payingToken === r.token}
                      className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer"
                    >
                      {payingToken === r.token ? "Redirecting…" : "Complete payment ($49)"}
                    </button>
                  )}
                  {r.status === "delivered" && (
                    <button
                      onClick={() => navigate(`/reports/${r.token}`)}
                      className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 cursor-pointer"
                    >
                      View report
                    </button>
                  )}
                  {(r.status === "paid" || r.status === "processing") && (
                    <span className="text-xs text-slate-500">
                      Report arrives within 24h of payment.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
